import { Navigate } from 'react-router-dom';
import { useEffect, useState } from 'react';

const AUTH_KEY = 'argo_admin_auth';
const LAST_ACTIVITY_KEY = 'admin_last_activity';
const SESSION_TIMEOUT = 30 * 60 * 1000; // 30 minutos
const WARNING_TIME = 5 * 60 * 1000; // 5 minutos antes de expirar

interface AdminGuardProps {
  children: React.ReactNode;
}

export default function AdminGuard({ children }: AdminGuardProps) {
  const [isAuthenticated, setIsAuthenticated] = useState(
    sessionStorage.getItem(AUTH_KEY) === 'true'
  );
  const [showWarning, setShowWarning] = useState(false);
  const [remainingMinutes, setRemainingMinutes] = useState(0);

  useEffect(() => {
    if (!isAuthenticated) return;

    // Actualizar última actividad en cada interacción
    const updateActivity = () => {
      sessionStorage.setItem(LAST_ACTIVITY_KEY, Date.now().toString());
      setShowWarning(false);
    };

    // Verificar timeout de sesión
    const checkSession = () => {
      const lastActivity = sessionStorage.getItem(LAST_ACTIVITY_KEY);
      if (!lastActivity) {
        sessionStorage.setItem(LAST_ACTIVITY_KEY, Date.now().toString());
        return;
      }

      const lastActivityTime = parseInt(lastActivity, 10);
      const now = Date.now();
      const timeSinceActivity = now - lastActivityTime;

      // Si pasaron 30 minutos, cerrar sesión
      if (timeSinceActivity >= SESSION_TIMEOUT) {
        sessionStorage.removeItem(AUTH_KEY);
        sessionStorage.removeItem(LAST_ACTIVITY_KEY);
        setIsAuthenticated(false);
        return;
      }

      // Si faltan 5 minutos o menos, mostrar advertencia
      const timeUntilExpiry = SESSION_TIMEOUT - timeSinceActivity;
      if (timeUntilExpiry <= WARNING_TIME) {
        setShowWarning(true);
        setRemainingMinutes(Math.ceil(timeUntilExpiry / 60000));
      } else {
        setShowWarning(false);
      }
    };

    // Eventos que actualizan la actividad
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
    events.forEach(event => {
      document.addEventListener(event, updateActivity);
    });

    // Verificar sesión cada 10 segundos
    checkSession();
    const interval = setInterval(checkSession, 10000);

    return () => {
      events.forEach(event => {
        document.removeEventListener(event, updateActivity);
      });
      clearInterval(interval);
    };
  }, [isAuthenticated]);

  const handleExtendSession = () => {
    sessionStorage.setItem(LAST_ACTIVITY_KEY, Date.now().toString());
    setShowWarning(false);
  };

  if (!isAuthenticated) {
    return <Navigate to="/admin/login" replace />;
  }

  return (
    <>
      {children}
      
      {/* Advertencia de expiración de sesión */}
      {showWarning && (
        <div className="fixed bottom-6 right-6 bg-white border-2 border-orange-400 rounded-xl shadow-2xl p-5 max-w-sm z-50 animate-bounce">
          <div className="flex items-start space-x-3">
            <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0">
              <i className="ri-time-line text-orange-600 text-xl"></i>
            </div>
            <div className="flex-1">
              <h3 className="font-bold text-gray-900 text-sm mb-1">
                Tu sesión está por expirar
              </h3>
              <p className="text-xs text-gray-600 mb-3">
                La sesión se cerrará en {remainingMinutes} {remainingMinutes === 1 ? 'minuto' : 'minutos'} por inactividad.
              </p>
              <button
                onClick={handleExtendSession}
                className="w-full py-2 bg-gradient-to-r from-teal-500 to-cyan-600 text-white text-sm font-semibold rounded-lg hover:shadow-lg transition-all cursor-pointer whitespace-nowrap"
              >
                Mantener sesión activa
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}